<template>
  <InnerContent>
    <div ref="shellRef" class="chat-shell">
      <div class="chat-content">
        <ConversationsList />
        <MessagesBlock />
        <ConversationContext v-if="isWideScreenRef" />
        <Sheet v-else :open="isChatContextSheetOpenRef" @update:open="setChatContextSheetOpen">
          <SheetContent
            side="right"
            class="conversation-context-sheet w-[88%] gap-0 p-0"
            data-test-id="chat-context-sheet"
          >
            <SheetHeader class="conversation-context-sheet__header">
              <SheetTitle>{{ t('chat.context.label') }}</SheetTitle>
            </SheetHeader>
            <ConversationContext in-sheet />
          </SheetContent>
        </Sheet>
      </div>
    </div>
  </InnerContent>
</template>

<script setup lang="ts">
import { useElementSize } from '@vueuse/core'
import { computed, ref, watch } from 'vue'

import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import ConversationContext from '@/features/chat/components/conversation-context/conversation-context.vue'
import ConversationsList from '@/features/chat/components/conversations-list/conversations-list.vue'
import MessagesBlock from '@/features/chat/components/messages-block/messages-block.vue'
import useChatContextSheet from '@/features/chat/hooks/use-chat-context-sheet.hook'
import useConversation from '@/features/chat/hooks/use-conversation.hook'
import groupsStore from '@/features/groups/stores/groups/groups.store'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import InnerContent from '@/features/ui/components/inner-content/inner-content.vue'
import { useI18n } from '@/lib/i18n'
import { selectGetCurrentRoute } from '@/store/route/route.selector'
import routeStore from '@/store/route/route.store'

const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)
const currentRouteRef = selectGetCurrentRoute(routeStore)
const roomIdRef = computed(() => (currentRouteRef.value?.params?.roomId as string | undefined) ?? null)
const { conversationRef } = useConversation(roomIdRef)

const { t } = useI18n()
// le contexte du binome a sa colonne a partir de 1152 px de place pour le chat
// ($chat-three-columns dans _chat-layout.scss), sinon il s'ouvre en tiroir
const CONTEXT_COLUMN_MIN_WIDTH = 1152
const shellRef = ref<HTMLElement | null>(null)
const { width: shellWidthRef } = useElementSize(shellRef)
const isWideScreenRef = computed(() => shellWidthRef.value >= CONTEXT_COLUMN_MIN_WIDTH)
const { isChatContextSheetOpenRef, setChatContextSheetOpen } = useChatContextSheet()
watch(roomIdRef, () => setChatContextSheetOpen(false))

// Chat tous programmes : le programme selectionne suit la conversation ouverte,
// y compris par lien direct ou depuis un e-mail de notification. Plus de
// redirection vers /chat au changement de programme.
watch(
  () => conversationRef.value?.programId,
  (programId) => {
    if (!programId || programId === selectedProgramIdRef.value) return
    programsStore.setSelectedProgramId(programId)
    const groupId = conversationRef.value?.groupId
    if (groupId) groupsStore.setSelectedGroupId(groupId)
  },
  { immediate: true },
)
</script>

<style lang="scss">
@import './chat-content.scss';
</style>
